import React, { useState, useEffect } from "react";
import { TextField, Select, Button } from "../ui";
import { FeatherChevronDown } from "@subframe/core";
import { journalsAPI } from "../services/api";
import { getAllVendors, getVendorById } from "../services/vendorApi";
import { SearchableDropdown, BankGlAccountDropdown } from "../shared/presentation/components";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";

/** Vendor item shape for the Received From dropdown (matches getAllVendors / getVendorById response) */
interface VendorDropdownItem {
  zoho_vendorid: string;
  vendor_legal_name: string;
  vendorid?: string;
  currency_id?: string;
}

interface CustomerCreditsOrRefundReceivedRecoProps {
  transactionDate: string;
  transactionAmount: number;
  onFromAccountChange: (accountId: string) => void;
  onDateChange: (date: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onVendorChange: (
    vendor: { vendor_id: string; vendor_name: string } | null,
  ) => void;
  zohoGl?: string;
}

const PAYMENT_MODES = [
  "Bank Remittance",
  "Bank Transfer",
  "Cash",
  "Cheque",
  "Credit Card",
  "UPI",
];

const CustomerCreditsOrRefundReceivedReco: React.FC<
  CustomerCreditsOrRefundReceivedRecoProps
> = ({
  transactionDate,
  onFromAccountChange,
  onDateChange,
  onPaymentModeChange,
  onReferenceNumberChange,
  onVendorChange,
  zohoGl,
}) => {
  const [fromAccountId, setFromAccountId] = useState<string>("");
  const [fromAccountName, setFromAccountName] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [selectedVendor, setSelectedVendor] = useState<{
    vendor_id: string;
    vendor_name: string;
  } | null>(null);
  /** Cache for vendors fetched by ID so we don't refetch when dropdown reopens */
  const [vendorsCache, setVendorsCache] = useState<
    Map<string, VendorDropdownItem>
  >(new Map());

  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  // Auto-select from account when zohoGl is provided
  useEffect(() => {
    if (!zohoGl || fromAccountId) return;
    journalsAPI
      .getGLMapByAccountId(zohoGl)
      .then((res) => {
        if (res.success && res.data) {
          const id = String(res.data.account_id);
          setFromAccountId(id);
          setFromAccountName(res.data.gl_name ?? "");
          onFromAccountChange(id);
        }
      })
      .catch(() => {});
  }, [zohoGl, fromAccountId, onFromAccountChange]);

  /**
   * Fetches vendors for the Received From dropdown.
   * - Selected vendor is always placed at the top when present in the list.
   * - If the selected vendor is not in the fetched list (e.g. from a previous search),
   *   it is fetched by ID, prepended to the list, and shown as selected.
   */
  const fetchVendors = async (
    searchTerm: string,
    limit: number,
    offset: number,
    selectedVendorId?: string,
    selectedVendorName?: string,
  ): Promise<{ items: VendorDropdownItem[]; hasMore?: boolean }> => {
    const response = await getAllVendors({
      search: searchTerm || undefined,
      limit,
      offset,
    });
    let items: VendorDropdownItem[] = (response.data ?? []).map((v: any) => ({
      zoho_vendorid: v.zoho_vendorid,
      vendor_legal_name: v.vendor_legal_name ?? "",
      vendorid: v.vendorid,
      currency_id: v.currency_id,
    }));

    const hasMore = Boolean(response.pagination?.has_more);

    if (offset === 0 && selectedVendorId && selectedVendorName) {
      const hasSelected = items.some(
        (v) => v.zoho_vendorid === selectedVendorId,
      );

      if (!hasSelected) {
        const cached = vendorsCache.get(selectedVendorId);
        if (cached) {
          items = [cached, ...items];
        } else {
          try {
            const byId = await getVendorById(selectedVendorId);
            if (byId?.data) {
              const mapped: VendorDropdownItem = {
                zoho_vendorid: byId.data.zoho_vendorid,
                vendor_legal_name: byId.data.vendor_legal_name ?? "",
                vendorid: byId.data.vendorid?.toString(),
                currency_id: byId.data.currency,
              };
              setVendorsCache((prev) => {
                const next = new Map(prev);
                next.set(selectedVendorId, mapped);
                return next;
              });
              items = [mapped, ...items];
            } else {
              items = [
                {
                  zoho_vendorid: selectedVendorId,
                  vendor_legal_name: selectedVendorName,
                },
                ...items,
              ];
            }
          } catch {
            items = [
              {
                zoho_vendorid: selectedVendorId,
                vendor_legal_name: selectedVendorName,
              },
              ...items,
            ];
          }
        }
      } else {
        const idx = items.findIndex(
          (v) => v.zoho_vendorid === selectedVendorId,
        );
        if (idx > 0) {
          const [selected] = items.splice(idx, 1);
          items = [selected, ...items];
        }
      }
    }

    return { items, hasMore };
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              From Account <span className="text-error-600">*</span>
            </span>
            <BankGlAccountDropdown
              preset="credits_refund"
              selectedAccountId={fromAccountId}
              selectedAccountName={fromAccountName}
              onSelect={(account: GLMappingEntity) => {
                const id = String(account.account_id);
                setFromAccountId(id);
                setFromAccountName(account.gl_name ?? "");
                onFromAccountChange(id);
              }}
              placeholder="Select account"
            />
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value;
                  setDate(v);
                  onDateChange(v);
                }}
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received Via
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={(v) => setPaymentMode(v)}
            >
              {PAYMENT_MODES.map((mode) => (
                <Select.Item key={mode} value={mode}>
                  {mode}
                </Select.Item>
              ))}
            </Select>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference Number
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Optional"
                value={referenceNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value;
                  setReferenceNumber(v);
                  onReferenceNumberChange(v);
                }}
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Received From (optional)
          </span>
          <SearchableDropdown<VendorDropdownItem>
            fetchItems={(search, limit, offset) =>
              fetchVendors(
                search,
                limit,
                offset,
                selectedVendor?.vendor_id,
                selectedVendor?.vendor_name,
              )
            }
            trigger={
              <Button
                className="w-full justify-between"
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                {selectedVendor?.vendor_name || "Select vendor (optional)"}
              </Button>
            }
            getSearchableText={(v) => v.vendor_legal_name ?? ""}
            renderItem={(v) => v.vendor_legal_name ?? ""}
            getItemKey={(v) => v.zoho_vendorid}
            onSelect={(v) => {
              const vendor = {
                vendor_id: v.zoho_vendorid,
                vendor_name: v.vendor_legal_name ?? "",
              };
              setSelectedVendor(vendor);
              onVendorChange(vendor);
            }}
            selectedKey={selectedVendor?.vendor_id}
            searchPlaceholder="Search vendors..."
            emptyMessage="No vendors found"
            noResultsMessage="No vendors match your search"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerCreditsOrRefundReceivedReco;
